import { localDateFromDate } from "@flockcover/flock-svc-core-types"
import makeUUID from "@shared/utils/helpers/makeUUID"
import { generateFleetEntryStatus } from "@shared/utils/helpers/generateFleetEntryStatus"
import { localDateFromString } from "@shared/utils/formatters/dates"
import { Status } from "@shared/types"
import { BaseFleetEntry, FleetEntry as LegacyFleetEntry, ResolvedFleetEntry, ResolvedFragment } from "./types"
import { createUniqueRef } from "./utils"

const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

export type MockBaseEntry = Omit<Partial<BaseFleetEntry>, "onCoverDate" | "offCoverDate"> & {
  onCoverDate?: string
  offCoverDate?: string | null
}

export const generateBaseFleetEntry = (entry?: MockBaseEntry) => {
  const registrationNumber = entry?.registrationNumber || `test1`
  const onCoverDate = entry?.onCoverDate
    ? localDateFromString(entry.onCoverDate)
    : localDateFromDate(new Date(`2021-01-01`), timeZone)
  const offCoverDate = entry?.offCoverDate ? localDateFromString(entry.offCoverDate) : null
  return {
    registrationNumber,
    onCoverDate,
    offCoverDate,
  }
}

export const generateBaseFleetEntries = (entries: Array<MockBaseEntry>) =>
  entries.map((it) => generateBaseFleetEntry(it))

export type MockAddedFleetEntry = MockBaseEntry &
  Omit<Partial<ResolvedFragment>, "vehicleId"> & {
    vehicleId?: string
  }
export const generateAddedFleetEntry = (entry?: MockAddedFleetEntry) => ({
  ...entry,
  vehicleId: entry?.vehicleId ? makeUUID(entry?.vehicleId) : undefined,
  ...generateBaseFleetEntry(entry),
})

export const generateAddedFleetEntries = (entries: ReadonlyArray<MockAddedFleetEntry>) =>
  entries.map((it) => generateAddedFleetEntry(it))

export type MockResolvedEntry = Omit<Partial<ResolvedFleetEntry>, "onCoverDate" | "offCoverDate" | "vehicleId"> & {
  vehicleId?: string
  onCoverDate?: string
  offCoverDate?: string | null
}

export const generateResolvedFleetEntry = (entry?: MockResolvedEntry): ResolvedFleetEntry => ({
  make: entry?.make || `Test`,
  model: entry?.model || `Mock`,
  abiGroup: entry?.abiGroup || 3,
  type: entry?.type || `car`,
  trim: entry?.trim || `fancy`,
  fuelType: entry?.fuelType || `petrol`,
  nominalStartYear: entry?.nominalStartYear || 2020,
  abiCode: entry?.abiCode || `123456`,
  grossWeight: entry?.grossWeight || null,
  numberOfSeats: entry?.numberOfSeats || 5,
  ...entry,
  ...generateBaseFleetEntry(entry),
  vehicleId: makeUUID(entry?.vehicleId),
})

export const generateResolvedFleetEntries = (entries?: ReadonlyArray<MockResolvedEntry>): Array<ResolvedFleetEntry> =>
  (entries || [])?.map((it) => generateResolvedFleetEntry(it))

export type MockFleetEntry = Omit<
  Partial<LegacyFleetEntry>,
  "vehicleId" | "uniqueRef" | "onCoverDate" | "offCoverDate" | "status" | "sentToMID"
> & {
  vehicleId?: string
  onCoverDate?: string
  offCoverDate?: string | null
  sentToMID?: string | null
  status?: Status
}

export const generateFleetEntry = (entry?: MockFleetEntry): Readonly<LegacyFleetEntry> => {
  const { registrationNumber, onCoverDate, offCoverDate, ...rest } = generateResolvedFleetEntry(entry)
  const status = entry?.status ? entry.status : generateFleetEntryStatus(onCoverDate, offCoverDate)
  const sentToMID = entry?.sentToMID ? localDateFromString(entry.sentToMID) : null
  return {
    ...entry,
    ...rest,
    id: entry?.id ?? makeUUID(),
    registrationNumber,
    onCoverDate,
    offCoverDate,
    status,
    sentToMID,
    uniqueRef: createUniqueRef(registrationNumber, onCoverDate, null),
    isConnected: entry?.isConnected ?? true,
    createdAt: entry?.createdAt ? localDateFromString(entry.createdAt) : null,
  }
}

export const modifyFleetEntry = (
  entry: LegacyFleetEntry,
  mods: {
    onCoverDate?: string
    offCoverDate?: string | null
    model?: string
    make?: string
    registrationNumber?: string
  }
) => {
  const onCoverDate = mods.onCoverDate ? localDateFromString(mods.onCoverDate) : entry.onCoverDate
  const offCoverDate =
    mods.offCoverDate === null ? null : mods.offCoverDate ? localDateFromString(mods.offCoverDate) : entry.offCoverDate
  const registrationNumber = mods.registrationNumber ?? entry.registrationNumber
  const make = mods.make ?? entry.make
  const model = mods.model ?? entry.model
  return {
    ...entry,
    registrationNumber,
    onCoverDate,
    offCoverDate,
    make,
    model,
  }
}

export const generateFleetEntries = (entries: Array<MockFleetEntry>): ReadonlyArray<LegacyFleetEntry> =>
  entries.map(generateFleetEntry)
